import React, { useCallback, useState } from "react";

import { Stack } from "@mui/material";
import { useNavigate } from "react-router-dom-v5-compat";

import usePostPortfolioPlanArchive from "@fartherfinance/frontend/api/Investment/hooks/usePostPortfolioPlanArchive";
import { PortfolioPlanId } from "@fartherfinance/frontend/api/Types";

import { InvestmentsPath } from "@src/config/routing/RouterPaths";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import Modal from "@src/sharedComponents/Modal/Modal";
import Text from "@src/sharedComponents/Typography/Text";
import { extractAxiosErrorMessage } from "@src/utils/axios";

import styles from "./ArchiveModelModal.module.css";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  portfolioPlanId: PortfolioPlanId;
};

const ArchiveModelModalV2: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  portfolioPlanId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const auth = useAdvisorRequestAuth();
  const postPortfolioPlanArchive = usePostPortfolioPlanArchive(auth);

  const statusNotification = useStatusNotification();

  const closeModal = useCallback(() => setIsOpen(false), [setIsOpen]);

  const archiveModel = useCallback(async () => {
    try {
      setIsLoading(true);
      await postPortfolioPlanArchive(portfolioPlanId);
      statusNotification("Model archived.", "Success");
      // invalidate cache and redirect to model marketplace
      navigate(`/Advisor/${InvestmentsPath}/ModelMarketplaceV2`);
    } catch (error) {
      const errorMessage = extractAxiosErrorMessage(error);

      statusNotification(errorMessage, "Error");
    } finally {
      setIsOpen(false);
      setIsLoading(false);
    }
  }, [
    navigate,
    setIsOpen,
    statusNotification,
    portfolioPlanId,
    postPortfolioPlanArchive,
  ]);

  return (
    <Modal
      closeModal={closeModal}
      modalStyle={{ width: 460 }}
      isOpen={isOpen}
      className={styles.modalContainer}
    >
      <Text variant="title2" style={{ paddingBottom: 8 }}>
        Once archived, this model cannot be used for rebalancing.
      </Text>
      <Text variant="body" color="textSubtle">
        To use this model in the future, you will need to reactivate it.
      </Text>
      <Stack direction={"row"} spacing={2} className={styles.buttonContainer}>
        <Button
          disabled={isLoading}
          isLoading={isLoading}
          sx={{ flex: 1 }}
          style={{ width: "100%" }}
          variant={"outlined"}
          buttonType={"secondary"}
          text={"Cancel"}
          onClick={closeModal}
        />

        <Button
          disabled={isLoading}
          isLoading={isLoading}
          variant={"contained"}
          sx={{ flex: 1 }}
          style={{ width: "100%" }}
          buttonType={"primary"}
          text={"Archive Model"}
          onClick={archiveModel}
        />
      </Stack>
    </Modal>
  );
};

export default ArchiveModelModalV2;
